import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { craPhaseService } from '../../services/phaseService';
import { CraPhase } from '../../types/Phase';
import { formatCurrency } from '../../utils/format';
import DateInput from '../common/DateInput';
import '../users/UserForm.css';

interface CraPhaseFormProps {
  productId: string;
  phase: CraPhase | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const CraPhaseForm: React.FC<CraPhaseFormProps> = ({ productId, phase, onSuccess, onCancel }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<CraPhase>({
    nombreFase: '',
    capObjetivoFase: undefined,
    capObjetivoActual: undefined,
    fechaInicioFase: '',
    fechaInicioActual: '',
    fechaCierreFase: '',
    valorContablePre: undefined,
    valorTicketUd: 10000,
    ticketsAEmitir: undefined,
    retencionIrpf: 19,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (phase) {
      setFormData(phase);
    }
  }, [phase]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const numericFields = ['capObjetivoFase', 'capObjetivoActual', 'valorContablePre', 'valorTicketUd', 'ticketsAEmitir', 'retencionIrpf'];

    setFormData((prev) => ({
      ...prev,
      [name]: numericFields.includes(name) ? (value ? parseFloat(value) : undefined) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (phase?.id) {
        await craPhaseService.update(productId, phase.id, formData);
        showToast('Fase CRA actualizada correctamente');
      } else {
        await craPhaseService.create(productId, formData);
        showToast('Fase CRA creada correctamente');
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar la fase CRA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-form phase-form">
      <h3>{phase ? 'Editar Fase CRA' : 'Nueva Fase CRA'}</h3>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <h4>Identificación</h4>
        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="nombreFase">Nombre de la Fase *</label>
            <input
              type="text"
              id="nombreFase"
              name="nombreFase"
              value={formData.nombreFase}
              onChange={handleChange}
              required
              maxLength={200}
            />
          </div>
          <div className="form-group">
            <label htmlFor="capObjetivoFase">Capital Objetivo</label>
            <input
              type="number"
              id="capObjetivoFase"
              name="capObjetivoFase"
              value={formData.capObjetivoFase || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="capObjetivoActual">Capital Objetivo Actual</label>
            <input
              type="number"
              id="capObjetivoActual"
              name="capObjetivoActual"
              value={formData.capObjetivoActual || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
            />
          </div>
        </div>

        <h4>Fechas</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fechaInicioFase">Fecha Inicio</label>
            <DateInput
              id="fechaInicioFase"
              name="fechaInicioFase"
              value={formData.fechaInicioFase || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaInicioActual">Fecha Inicio Actual</label>
            <DateInput
              id="fechaInicioActual"
              name="fechaInicioActual"
              value={formData.fechaInicioActual || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaCierreFase">Fecha Cierre</label>
            <DateInput
              id="fechaCierreFase"
              name="fechaCierreFase"
              value={formData.fechaCierreFase || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        <h4>Condiciones</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="valorContablePre">Valor Contable Pre</label>
            <input
              type="number"
              id="valorContablePre"
              name="valorContablePre"
              value={formData.valorContablePre || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="valorTicketUd">Valor Ticket Ud.</label>
            <input
              type="number"
              id="valorTicketUd"
              name="valorTicketUd"
              value={formData.valorTicketUd || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="ticketsAEmitir">Tickets a Emitir</label>
            <input
              type="number"
              id="ticketsAEmitir"
              name="ticketsAEmitir"
              value={formData.ticketsAEmitir || ''}
              onChange={handleChange}
              min="0"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="retencionIrpf">Retencion IRPF (%)</label>
            <input
              type="number"
              id="retencionIrpf"
              name="retencionIrpf"
              value={formData.retencionIrpf || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
              max="100"
            />
          </div>
        </div>

        {phase && (
          <>
            <h4>Métricas</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Total Captado</label>
                <input type="text" value={formatCurrency(formData.totalCaptado)} disabled />
              </div>
              <div className="form-group">
                <label>Total Devuelto</label>
                <input type="text" value={formatCurrency(formData.totalDevuelto)} disabled />
              </div>
              <div className="form-group">
                <label>Num Inversores</label>
                <input type="text" value={formData.numInversores || 0} disabled />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Num Tickets</label>
                <input type="text" value={formData.numTickets || 0} disabled />
              </div>
              <div className="form-group">
                <label>Valor Actual Ticket</label>
                <input type="text" value={formatCurrency(formData.valorActualTicket)} disabled />
              </div>
              <div className="form-group">
                <label>% Reval. Acumulada</label>
                <input type="text" value={formatPercent(formData.porcentajeRevalAcumulada)} disabled />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Repartos Realizados</label>
                <input type="text" value={formatCurrency(formData.repartosRealizados)} disabled />
              </div>
              <div className="form-group">
                <label>Liq. Final Pendiente</label>
                <input type="text" value={formatCurrency(formData.liqFinalPendiente)} disabled />
              </div>
            </div>
          </>
        )}

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary" disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : phase ? 'Actualizar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
};

const formatPercent = (value?: number) => {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(2)}%`;
};

export default CraPhaseForm;
